import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./img-swiper.module.scss";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import placeholderImg from "../../images/slider-placeholder.png";
import SlideButton from "./slide-button/slide-button";
import { getAttachmentImageUrl } from "../../services/api";
import { createPortal } from "react-dom";
import { Attachment } from "../../interfaces/feature-info";
import { isRTL } from "../../utils/rtl";

interface ImgSwiperProps {
  images?: Attachment[];
}

export type ImageUrl = {
  url: string;
  isPlaceholder: boolean;
  id?: number;
};

const ImgSwiper: FC<ImgSwiperProps> = ({ images }) => {
  const { t } = useTranslation();
  const [imageUrls, setImageUrls] = useState<ImageUrl[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideNumber, setSlideNumber] = useState(1);

  const header = document.getElementById("app-header");

  const headerHeight = header?.offsetHeight;

  useEffect(() => {
    let isMounted = true;

    const fetchImageUrls = async () => {
      if (images && images.length > 0) {
        const acceptedImages: ImageUrl[] = [];

        for (const img of images) {
          try {
            const result = await getAttachmentImageUrl(img.id);
            const url = typeof result === "string" ? result : result.url;

            acceptedImages.push({ url, isPlaceholder: false });
          } catch (error) {
            console.error(error);
          }
        }

        if (isMounted) {
          setImageUrls(acceptedImages);
        }
      } else {
        if (isMounted) {
          setImageUrls([]);
        }
      }
    };

    fetchImageUrls();

    return () => {
      isMounted = false;
      imageUrls.forEach((image) => {
        if (image.url.startsWith("blob:")) {
          URL.revokeObjectURL(image.url);
        }
      });
    };
  }, [images]);

  const openModal = (index: number) => {
    setCurrentIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentIndex(0);
  };

  const nextImage = () => {
    setCurrentIndex((prevIndex) => {
      let nextIdx = (prevIndex + 1) % imageUrls.length;
      while (imageUrls[nextIdx].isPlaceholder && nextIdx !== prevIndex) {
        nextIdx = (nextIdx + 1) % imageUrls.length;
      }
      return nextIdx;
    });
  };

  const prevImage = () => {
    setCurrentIndex((prevIndex) => {
      let prevIdx = (prevIndex - 1 + imageUrls.length) % imageUrls.length;
      while (imageUrls[prevIdx].isPlaceholder && prevIdx !== prevIndex) {
        prevIdx = (prevIdx - 1 + imageUrls.length) % imageUrls.length;
      }
      return prevIdx;
    });
  };

  const hasRealImages = imageUrls.length > 0 && !imageUrls[0].isPlaceholder;

  const modalRoot = document.getElementById("react-modals");

  const breakpoints = {
    1441: { slidesPerView: 1.65 },
    0: { slidesPerView: 1.4 },
  };

  return (
    <>
      {hasRealImages ? (
        <Swiper
          key={isRTL() ? "rtl" : "ltr"}
          spaceBetween={10}
          initialSlide={slideNumber - 1}
          breakpoints={breakpoints}
          dir={isRTL() ? "rtl" : "ltr"}
        >
          {slideNumber != 1 && (
            <SlideButton
              key={`${isRTL() ? "rtl" : "ltr"}-${slideNumber}-prev`}
              slideNumber={slideNumber}
              setSlideNumber={setSlideNumber}
            />
          )}
          {imageUrls.map((image, index) => (
            <SwiperSlide key={index}>
              <img
                src={image.url}
                onClick={() => openModal(index)}
                style={{
                  width: "212px",
                  height: "100px",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
                alt={t("img-swiper.slide.alt", { index: index + 1 })}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = placeholderImg;
                }}
              />
            </SwiperSlide>
          ))}
          {slideNumber < imageUrls.length && (
            <SlideButton
              key={`${isRTL() ? "rtl" : "ltr"}-${slideNumber}-next`}
              slideNumber={slideNumber}
              setSlideNumber={setSlideNumber}
              next={true}
            />
          )}
        </Swiper>
      ) : (
        <img
          src={placeholderImg}
          style={{
            width: "212px",
            height: "100px",
            borderRadius: "4px",
          }}
          alt={t("img-swiper.placeholder.alt")}
        />
      )}

      {isModalOpen &&
        hasRealImages &&
        createPortal(
          <div className={styles.modal} style={{ top: headerHeight }}>
            <div
              className={styles.backdrop}
              style={{ top: headerHeight }}
            ></div>
            <div className={styles.modalContent}>
              {currentIndex > 0 && (
                <button
                  className={`${styles.arrowButton} ${styles.arrowButtonLeft}`}
                  onClick={prevImage}
                  aria-label={t("img-swiper.prevButton.ariaLabel")}
                ></button>
              )}

              <img
                src={imageUrls[currentIndex].url}
                style={{ height: "670px" }}
                alt={t("img-swiper.fullSizeImage.alt", {
                  index: currentIndex + 1,
                })}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = placeholderImg;
                }}
              />

              {currentIndex < imageUrls.length - 1 && (
                <button
                  className={`${styles.arrowButton} ${styles.arrowButtonRight}`}
                  onClick={nextImage}
                  aria-label={t("img-swiper.nextButton.ariaLabel")}
                ></button>
              )}

              <button
                className={styles.closeButton}
                onClick={closeModal}
                aria-label={t("img-swiper.closeButton.ariaLabel")}
                style={{ top: `${headerHeight! + 36}px` }}
              ></button>
            </div>
          </div>,
          modalRoot!,
        )}
    </>
  );
};

export default ImgSwiper;
